import { Component, Inject, OnInit, Optional } from '@angular/core';
import { ListButton } from '@app/core/components/list/list.component';
import { UserStore } from '@app/core/services/user.store';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { RiskConsequence, RiskContext, RiskCount, RiskItem, RiskLevel, RiskProbability, RiskService } from '@app/services/risk.service';

@Component({ selector: 'tt-risk-elements', standalone: false, templateUrl: './risk-elements.component.html', styleUrls: ['./risk-elements.component.css'] })
export class RiskElementsComponent implements OnInit {
    public params: RiskContext = { argtype: '', argvalue: '' };
    public probabilities: RiskProbability[] = [];
    public consequences: RiskConsequence[] = [];
    public levels: RiskLevelView[] = [];
    public rows: RiskMatrixRow[] = [];
    public items: RiskItem[] = [];
    public cellClicked = 0;
    public showDescriptions = false;
    public error = '';
    public loading = false;
    public readonly rowButtons: ListButton<RiskItem>[] = [{ id: 'newtab', icon: 'fas fa-external-link', type: 'secondary', onClick: ({ item }) => { if (item.item_state) this.navigator?.newTab({ to: this.destination(item.item_state), parms: item.item_parms }); } }];
    constructor(private risk: RiskService, public user: UserStore,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) { this.navigator = useNavigatorFor(navigator, this); }
    public get smallFont(): string { return `${Number.parseInt(this.user.fontSize, 10) - 3}px`; }
    public consequenceName(consequence: number): string {
        return this.consequences[consequence - 1]?.consequence_name ?? '';
    }
    public async ngOnInit(): Promise<void> {
        this.params = { argtype: String(this.navigator?.params['argtype'] ?? ''), argvalue: String(this.navigator?.params['argvalue'] ?? '') };
        await Promise.all([
            this.risk.listProbabilities().then(rows => this.probabilities = rows).catch(error => this.showError(error)),
            this.risk.listConsequences().then(rows => this.consequences = rows).catch(error => this.showError(error)),
            this.risk.listLevels().then(rows => this.levels = rows.map(row => ({ ...row, ...this.colours(`_${row.risklevel_no}`) }))).catch(error => this.showError(error)),
            this.loadItems('0', '0'),
            this.risk.listCounts(this.params).then(counts => this.prepareMatrix(counts)).catch(error => this.showError(error)),
        ]);
    }
    private prepareMatrix(counts: RiskCount[]): void {
        this.rows = [5, 4, 3, 2, 1].map(consequence => ({ consequence, cells: [1, 2, 3, 4, 5].map(probability => {
            let count: string | number = 0;
            for (const item of counts) if (Number(item.probability_no) === probability && Number(item.consequence_no) === consequence) count = item.nbr;
            return { probability, consequence, count, showCount: Number(count) > 0, ...this.colours(`${probability}_${consequence}`) };
        }) }));
    }
    private colours(key: string): RiskColours {
        // Preserve the matrix's explicit lookup; its 2x2 cell is green even though the editor's score 4 is yellow.
        if (['1_1', '1_2', '1_3', '2_1', '2_2', '3_1', '_4'].includes(key)) return { background: '#0F9F0F', color: '#FFFFFF' };
        if (['1_4', '1_5', '2_3', '2_4', '3_2', '3_3', '4_1', '4_2', '5_1', '_3'].includes(key)) return { background: '#F2E228', color: '#000000' };
        if (['2_5', '3_4', '4_3', '5_2', '_2'].includes(key)) return { background: '#FFAC22', color: '#000000' };
        return { background: '#B91114', color: '#ffffff' };
    }
    public async filterCell(cell: RiskMatrixCell): Promise<void> { if (this.loading) return; ++this.cellClicked; await this.loadItems(cell.probability, cell.consequence); }
    public async showAll(): Promise<void> { if (this.loading) return; this.cellClicked = 0; await this.loadItems('0', '0'); }
    private async loadItems(probability_no: string | number, consequence_no: string | number): Promise<void> {
        this.loading = true;
        this.items = [];
        try { this.items = await this.risk.listElements({ ...this.params, probability_no, consequence_no }); }
        catch (error) { this.showError(error); }
        finally { this.loading = false; }
    }
    private destination(state: string): string { return state === 'riskelement' ? 'riskelement-test' : state; }
    public open(event: { item: RiskItem; event: MouseEvent }): void {
        if (!event.item.item_state || event.item.item_path === '') return;
        const target = { to: this.destination(event.item.item_state), parms: event.item.item_parms };
        if (event.event.ctrlKey) this.navigator?.newTab(target); else this.navigator?.go(target);
    }
    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
}
interface RiskColours { background: string; color: string; }
interface RiskLevelView extends RiskLevel, RiskColours {}
interface RiskMatrixCell extends RiskColours { probability: number; consequence: number; count: string | number; showCount: boolean; }
interface RiskMatrixRow { consequence: number; cells: RiskMatrixCell[]; }
